/**
 * The `File` objects behind a message that is still in flight.
 *
 * They cannot live in Redux — a `File` is not serialisable, and the store is
 * persisted — but a retry needs the actual bytes, not the filenames the pending
 * row carries. Keeping them here, keyed by the same client token the send uses,
 * is what lets a retry resend the attachments instead of quietly posting the
 * text on its own.
 *
 * A reload empties this map while the failed message survives in the store, so
 * `recall` returning `undefined` is a real state the caller has to handle: the
 * files are genuinely gone and the user has to attach them again. Treating that
 * as "no attachments" would send a stripped message under the same token.
 */
const inFlight = new Map<string, File[]>()

export const rememberAttachments = (token: string, files: File[]): void => {
  if (files.length) inFlight.set(token, files)
}

/** The files for this token, or `undefined` if they were never kept or are gone. */
export const recallAttachments = (token: string): File[] | undefined =>
  inFlight.get(token)

export const forgetAttachments = (token: string): void => {
  inFlight.delete(token)
}

/** Whether this token was sent with attachments at all. */
export const hadAttachments = (token: string): boolean => inFlight.has(token)
